<i18n src="./translations"></i18n>

<template>
  <div class="product-details">
    <div class="pdp">
      <div class="main-top">
        <h1 class="title">{{ product.brandName }} {{ product.name }}</h1>

        <div class="article-nr">
          {{ $t('product-details-26.article-number') }}:
          {{ product.displaySku }}
        </div>

        <PdpVehicleNotification
          v-if="!product.isDropShipment"
          class="pdp-vehicle-notification-top"
          :gtm-class-name="'gtm--pdp-vehicle-notification'"
          :click-tracking="{
            clickType: 'pdp_vehicle_notification',
            context: { product_id: product.productId, placement: 'top' },
          }"
          :vehicle-compatibility="productVehicleCompatibility"
          :vehicle-name="vehicleName"
          :related-items-link="relatedItemsLink"
          :is-car-selected="isCarSelected"
          :car-attributes="carAttributes"
          attributes-variant="chips"
          @scroll-to-attributes="openDescriptionTab"
        />

        <div class="img-slider">
          <AdvancedImage
            v-if="product.brandLogoUrl"
            :is-lazy-component="false"
            :src="product.brandLogoUrl || ''"
            :alt="product.brandName"
            :config="brandConfig"
            class="brand-logo"
            data-testid="pdp-brandLogo"
          />
          <ProductImageSlider :title="product.name" :images="product.images" />
        </div>
      </div>

      <PdpSidebar
        class="sidebar"
        :pdp-car-bar="pdpCarBar"
        :product="product"
        :related-items-link="relatedItemsLink"
      />

      <div class="main-bottom">
        <PdpTabs26
          ref="pdpTabs"
          class="qa-tabInformation"
          tabindex="0"
          :product="product"
          :restrictions="restrictions"
          :features="features"
          :car-attributes="carAttributes"
          :is-car-selected="isCarSelected"
          :vehicle-compatibility="productVehicleCompatibility"
          :vehicle-name="vehicleName"
          :related-items-link="relatedItemsLink"
        />

        <ProductAvailability
          v-show="isEmployee"
          :product-availability="product.productAvailability"
        />
      </div>
    </div>

    <CategoryRecommendations
      v-if="!!recommendations.length"
      :items="recommendations"
    />

    <PdpFooter class="pdp-footer" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import globalMixin from 'Libs/mixins/globalMixin';

import PdpSidebar from 'Components/03-organisms/product-details-26/subcomponents/pdp-sidebar/PdpSidebar';
import PdpTabs26 from 'Components/03-organisms/product-details-26/subcomponents/pdp-tabs-26/PdpTabs26';
import ProductImageSlider from 'Components/02-molecules/product-image-slider/ProductImageSlider';
import CategoryRecommendations from 'Components/02-molecules/category-recommendations/CategoryRecommendations';
import PdpFooter from 'Components/03-organisms/product-details-26/subcomponents/pdp-footer/PdpFooter';
import AdvancedImage from 'Components/01-atoms/advanced-image/AdvancedImage';
import PdpVehicleNotification from 'Components/02-molecules/pdp-vehicle-notification/PdpVehicleNotification';

export default {
  name: 'ProductDetails26',
  components: {
    PdpSidebar,
    ProductImageSlider,
    PdpTabs26,
    CategoryRecommendations,
    PdpFooter,
    AdvancedImage,
    PdpVehicleNotification,
  },
  mixins: [globalMixin],
  props: {
    product: {
      type: Object,
      required: true,
    },
    categoryRecommendations: {
      type: Array,
      default: () => [],
    },
    pdpCarBar: {
      type: Object,
      default: () => ({}),
    },
    breadcrumbs: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      brandConfig: {
        q: 60,
        w: 100,
      },
    };
  },
  computed: {
    ...mapState('core', {
      staticUrl: ({ salesChannel }) => salesChannel.staticUrl,
      isEmployee: ({ isEmployee }) => isEmployee,
    }),
    ...mapState('carSelection', {
      vehicleName: ({ data: { vehicle = {} } }) => {
        const { manufacturer, model, type, bodyCodes } = vehicle;
        const selectionName = `${manufacturer} ${model} ${type}`;
        if (bodyCodes && bodyCodes.length > 0) {
          return `${selectionName} (${[...bodyCodes].sort().join(', ')})`;
        }
        return selectionName;
      },
      isCarSelected: ({ data: { carDisplayName } }) => !!carDisplayName,
    }),
    restrictions() {
      return this.product.attributes.filter((attribute) => attribute.isTop);
    },
    features() {
      return this.product.attributes.filter((attribute) => !attribute.isTop);
    },
    carAttributes() {
      return this.restrictions
        .filter(({ id }) => id !== 'block_separation_8')
        .map(({ id, title, value, isTop }) => ({
          id,
          label: title,
          info: value,
          isTop,
        }));
    },
    recommendations() {
      let categories = [];
      if (this.categoryRecommendations.length) {
        categories = this.categoryRecommendations.map((item) => {
          if (item.imageUrl) {
            !item.imageUrl.includes(this.staticUrl)
              ? (item.imageUrl = `${this.staticUrl}/media-library/${item.imageUrl}`)
              : null;
          }
          return item;
        });
      }
      return categories;
    },
    relatedItemsLink() {
      const { breadcrumbs } = this.breadcrumbs;
      if (breadcrumbs) {
        const relatedItemsIndex = breadcrumbs.length - 2;
        if (relatedItemsIndex >= 0) {
          return breadcrumbs[relatedItemsIndex].url;
        }
      }

      return '/';
    },
    productVehicleCompatibility() {
      return this.pdpCarBar?.key || 'UNIVERSAL_PRODUCT';
    },
  },
  methods: {
    openDescriptionTab() {
      const ref = this.$refs.pdpTabs;
      if (!ref || !ref.$el) return;

      if (typeof window === 'undefined') return;

      if (typeof ref.$el.scrollIntoView === 'function') {
        ref.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'variables/breakpoints';

.pdp-vehicle-notification-top {
  margin-bottom: var(--space-2);
}

.product-details {
  max-width: 100%;
}

.pdp {
  display: flex;
  flex-direction: column;
  position: relative;
  gap: var(--space-3);

  .main-top {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);

    .title {
      font-size: var(--font-size-L);
      font-weight: 700;
      hyphens: auto;
      overflow-wrap: break-word;
    }

    .article-nr {
      font-size: var(--font-size-SM);
      color: var(--color-charcoal-gray);
    }

    .img-slider {
      position: relative;

      .brand-logo {
        width: 65px;
        height: 40px;
        position: absolute;
        top: 0;
        right: 0;
        z-index: 10;
      }
    }
  }

  @media #{$_mediaLUp} {
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(370px, 1fr);
    grid-template-areas:
      'top side'
      'bottom side';

    .main-top {
      grid-area: top;
    }

    .sidebar {
      grid-area: side;
    }

    .main-bottom {
      grid-area: bottom;
    }

    .main-top {
      .title {
        font-size: var(--font-size-XL);
      }
    }
  }

  @media #{$_mediaMDown} {
    max-width: 100vw;

    .sidebar {
      width: 100%;
      margin-top: var(--space-3);
    }
  }
}

.pdp-footer {
  display: none;

  @media #{$_mediaMDown} {
    display: block;
  }
}
</style>
